import { appStorage } from '@/utils/storage';
import { IWindowCartItem, IAssistantCart, IAssistantMessage } from "../types";
import { calculatePriceFromMessages } from './configurationPriceCalculation';
import { FOIL_SURCHARGE_RESOLVER } from '../constants';

const MULTI_CART_STORAGE_KEY = 'multi_customer_cart';

/**
 * Strip heavy fields from messages before persisting to localStorage.
 * `question.options` contains the full list of every available option for
 * that question step — this is the main cause of QuotaExceededError.
 * We only need the selected answers (`response.answer`) for display; the
 * full options list is re-fetched from config on demand.
 */
const slimMessages = (messages: IAssistantMessage[]): IAssistantMessage[] =>
    messages.map(msg => ({
        ...msg,
        question: msg.question
            ? { ...msg.question, options: undefined }
            : msg.question,
    }));

const slimCartForStorage = (cart: IAssistantCart): IAssistantCart => ({
    ...cart,
    windowConfiguration: cart.windowConfiguration.map(item => ({
        ...item,
        messages: slimMessages(item.messages ?? []),
    })),
});

/**
 * Multi-customer cart structure
 * Format: { [customerId]: IAssistantCart }
 */
export interface IMultiCustomerCart {
    [customerId: string]: IAssistantCart;
}

/**
 * Load all customer carts from localStorage
 */
export const loadAllCustomerCarts = (): IMultiCustomerCart => {
    const allCarts = appStorage.get<IMultiCustomerCart>(MULTI_CART_STORAGE_KEY);
    return allCarts || {};
};

/**
 * Save all customer carts to localStorage.
 * Returns true on success, false if the save failed (e.g. quota exceeded).
 */
export const saveAllCustomerCarts = (allCarts: IMultiCustomerCart): boolean => {
    try {
        // Strip question.options from messages before storing — they are the main
        // cause of QuotaExceededError (entire option catalogue stored per item).
        const slim: IMultiCustomerCart = Object.fromEntries(
            Object.entries(allCarts).map(([id, cart]) => [id, slimCartForStorage(cart)])
        );
        const serialized = JSON.stringify(slim);
        window.localStorage.setItem(`react_${MULTI_CART_STORAGE_KEY}`, serialized);
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Load cart for a specific customer
 */
export const loadCustomerCart = (customerId: string): IAssistantCart | null => {
    if (!customerId) {
        return null;
    }

    const allCarts = loadAllCustomerCarts();
    const cart = allCarts[customerId];

    if (cart) {
    }

    return cart || null;
};

/**
 * Save cart for a specific customer.
 * Throws if the save fails so callers can handle the error.
 */
export const saveCustomerCart = (customerId: string, cart: IAssistantCart): void => {
    if (!customerId) {
        return;
    }

    const allCarts = loadAllCustomerCarts();
    allCarts[customerId] = cart;
    const saved = saveAllCustomerCarts(allCarts);
    if (!saved) {
        throw new Error(`Failed to persist cart for customer ${customerId} — storage quota may be exceeded`);
    }
};

/**
 * Clear cart for a specific customer
 */
export const clearCustomerCart = (customerId: string): void => {
    if (!customerId) {
        return;
    }

    const allCarts = loadAllCustomerCarts();
    delete allCarts[customerId];
    saveAllCustomerCarts(allCarts);

};

/**
 * Clear all customer carts
 */
export const clearAllCustomerCarts = (): void => {
    appStorage.remove(MULTI_CART_STORAGE_KEY);
};

/**
 * Get list of all customer IDs with carts
 */
export const getAllCustomerIdsWithCarts = (): string[] => {
    const allCarts = loadAllCustomerCarts();
    return Object.keys(allCarts);
};

/**
 * Get count of carts
 */
export const getCartCount = (): number => {
    const allCarts = loadAllCustomerCarts();
    return Object.keys(allCarts).length;
};

/**
 * Get total items across all carts
 */
export const getTotalItemsAcrossAllCarts = (): number => {
    const allCarts = loadAllCustomerCarts();
    return Object.values(allCarts).reduce(
        (total, cart) => total + cart.windowConfiguration.length,
        0
    );
};

/**
 * Add item to customer's cart
 */
export const addItemToCustomerCart = (
    customerId: string,
    item: Omit<IWindowCartItem, "id" | "timestamp" | "quantity">,
    existingCart?: IAssistantCart
): IAssistantCart => {
    if (!customerId) {
        throw new Error('customerId is required');
    }

    // Load existing cart or use provided cart
    let cart = existingCart || loadCustomerCart(customerId);

    if (!cart) {
        throw new Error('Cart not found');
    }

    // Remove "Text Value: " prefix from window name if present
    const cleanWindowName = item.windowName.replace(/^Text Value:\s*/i, "");

    const newItem: IWindowCartItem = {
        ...item,
        windowName: cleanWindowName,
        id: `cart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        quantity: 1,
        timestamp: Date.now(),
    };

    const updatedCart = {
        ...cart,
        windowConfiguration: [...cart.windowConfiguration, newItem],
    };

    saveCustomerCart(customerId, updatedCart);
    return updatedCart;
};

/**
 * Remove item from customer's cart
 */
export const removeItemFromCustomerCart = (
    customerId: string,
    itemId: string
): IAssistantCart | null => {
    const cart = loadCustomerCart(customerId);
    if (!cart) return null;

    const updatedCart = {
        ...cart,
        windowConfiguration: cart.windowConfiguration.filter(
            (item) => item.id !== itemId
        ),
    };

    saveCustomerCart(customerId, updatedCart);
    return updatedCart;
};

/**
 * Update item quantity in customer's cart
 */
export const updateItemQuantityInCustomerCart = (
    customerId: string,
    itemId: string,
    quantity: number
): IAssistantCart | null => {
    if (quantity < 1) {
        return null;
    }

    const cart = loadCustomerCart(customerId);
    if (!cart) return null;

    const updatedCart = {
        ...cart,
        windowConfiguration: cart.windowConfiguration.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
        ),
    };

    saveCustomerCart(customerId, updatedCart);
    return updatedCart;
};

/**
 * Update window name in customer's cart
 */
export const updateWindowNameInCustomerCart = (
    customerId: string,
    itemId: string,
    newName: string
): IAssistantCart | null => {
    if (!newName || newName.trim().length === 0) {
        return null;
    }

    const cart = loadCustomerCart(customerId);
    if (!cart) return null;

    const updatedCart = {
        ...cart,
        windowConfiguration: cart.windowConfiguration.map((item) =>
            item.id === itemId ? { ...item, windowName: newName.trim() } : item
        ),
    };

    saveCustomerCart(customerId, updatedCart);
    return updatedCart;
};

/**
 * Update custom discount for customer's cart
 */
export const updateCustomDiscountInCustomerCart = (
    customerId: string,
    customDiscountPercentage: number
): IAssistantCart | null => {
    const cart = loadCustomerCart(customerId);
    if (!cart) return null;

    const updatedCart = {
        ...cart,
        customDiscountPercentage,
    };

    saveCustomerCart(customerId, updatedCart);
    return updatedCart;
};

/**
 * Duplicate cart item in customer's cart
 */
export const duplicateCartItemInCustomerCart = (
    customerId: string,
    itemId: string,
    newWindowName: string,
    overrideImages?: {
        propertyImageUrl?: string;
        blendedImageUrl?: string;
        onlineBlendedImageUrl?: string;
        windowAnnotationId?: string;
        /** Full box coordinates of the selected window (x, y, width, height) */
        selectedWindowBox?: { x: number; y: number; width: number; height: number; label?: string };
        /** All blended window boxes — populated when multiple windows were blended at once */
        selectedWindowBoxes?: Array<{ id: string; label: string; x: number; y: number; width: number; height: number }>;
    },
): IAssistantCart | null => {
    const cart = loadCustomerCart(customerId);
    if (!cart) return null;

    const itemToDuplicate = cart.windowConfiguration.find(
        (item) => item.id === itemId
    );

    if (!itemToDuplicate) {
        return null;
    }

    // Strip "Specials Foil Surchage" from duplicated messages — flat one-off charge for original only
    const duplicatedMessages = itemToDuplicate.messages.reduce<IAssistantMessage[]>((acc, msg: any) => {
        if (msg.type === 'price-surge' && msg.surgeItems?.some((s: any) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)) {
            const filteredItems = msg.surgeItems.filter((s: any) => s.resolverValue !== FOIL_SURCHARGE_RESOLVER);
            if (filteredItems.length === 0) return acc;
            acc.push({ ...msg, surgeItems: filteredItems });
        } else {
            acc.push(msg);
        }
        return acc;
    }, []);
    const duplicatedPrice = calculatePriceFromMessages(duplicatedMessages, 'GBP');

    // Resolve the primary window box: prefer first entry from selectedWindowBoxes, fall back to selectedWindowBox
    const primaryBox = overrideImages?.selectedWindowBoxes?.[0] ?? overrideImages?.selectedWindowBox;
    const allBoxes = overrideImages?.selectedWindowBoxes;

    const duplicatedItem: IWindowCartItem = {
        ...itemToDuplicate,
        id: `cart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        windowName: newWindowName,
        quantity: overrideImages?.selectedWindowBoxes?.length ?? 1,
        timestamp: Date.now(),
        messages: duplicatedMessages,
        price: duplicatedPrice,
        // Clear blend/annotation data — duplicate starts fresh until user blends it
        blendedImageUrl: undefined,
        onlineBlendedImageUrl: undefined,
        windowAnnotationId: undefined,
        windows: undefined,
        ...(overrideImages && {
            propertyImageUrl: overrideImages.propertyImageUrl ?? itemToDuplicate.propertyImageUrl,
            blendedImageUrl: overrideImages.blendedImageUrl ?? undefined,
            onlineBlendedImageUrl: overrideImages.onlineBlendedImageUrl ?? undefined,
            ...(overrideImages.windowAnnotationId !== undefined && {
                windowAnnotationId: overrideImages.windowAnnotationId,
                // Use the selected window's actual box coordinates so editing the
                // duplicate blends into the correct window, not the source window.
                window: primaryBox
                    ? {
                        ...itemToDuplicate.window,
                        id: overrideImages.windowAnnotationId,
                        x: primaryBox.x,
                        y: primaryBox.y,
                        width: primaryBox.width,
                        height: primaryBox.height,
                        ...(primaryBox.label !== undefined && { label: primaryBox.label }),
                    }
                    : {
                        ...itemToDuplicate.window,
                        id: overrideImages.windowAnnotationId,
                    },
                // When multiple windows were blended at once, store them all
                ...(allBoxes && allBoxes.length > 1 && {
                    windows: allBoxes.map(b => ({
                        id: b.id,
                        label: b.label,
                        x: b.x,
                        y: b.y,
                        width: b.width,
                        height: b.height,
                    })),
                }),
            }),
        }),
    };

    const updatedCart = {
        ...cart,
        windowConfiguration: [...cart.windowConfiguration, duplicatedItem],
    };

    try {
        saveCustomerCart(customerId, updatedCart);
    } catch (error) {
    }
    return updatedCart;
};

/**
 * Migrate old single-customer carts to new multi-customer structure
 * This function should be called once during app initialization
 */
export const migrateOldCartsToMultiCustomer = (): void => {
    const CART_STORAGE_PREFIX = 'assistant_cart_';
    const allKeys = Object.keys(localStorage);
    const oldCartKeys = allKeys.filter(key => key.startsWith(CART_STORAGE_PREFIX));

    if (oldCartKeys.length === 0) {
        return;
    }

    const multiCart: IMultiCustomerCart = loadAllCustomerCarts();
    let migratedCount = 0;

    oldCartKeys.forEach(key => {
        const customerId = key.replace(CART_STORAGE_PREFIX, '');
        const oldCart = appStorage.get<IAssistantCart>(key);

        if (oldCart && oldCart.customer && !multiCart[customerId]) {
            multiCart[customerId] = oldCart;
            migratedCount++;
            // Optionally remove old cart
            // appStorage.remove(key);
        }
    });

    if (migratedCount > 0) {
        saveAllCustomerCarts(multiCart);
    }
};
